import React, { CSSProperties } from 'react';
import { FeatureId } from 'features/definitions';
import { useFeature } from 'Hooks/useFeature';
import UpgradeToUnlockCTA from 'pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA';
import { PlanType } from 'modules/Const';
import CardSymphony from 'elements/CardSymphony';
import Colors from 'modules/Colors';

interface FeatureGateProps {
    /** The feature to check access for */
    feature: FeatureId;
    /** Optional fallback content to show when feature is not available */
    fallback?: React.ReactNode;
    /** Whether to show the upgrade CTA when feature is not available */
    showUpgrade?: boolean;
    /** Custom upgrade source for tracking */
    upgradeSource?: string;
    /** Custom upgrade call to action text */
    upgradeCallToAction?: string;
    /** Custom button text for the upgrade CTA */
    buttonText?: string;
    /** Children to render when feature is available */
    children?: React.ReactNode;
    /** Whether to wrap content in a banner card */
    showBanner?: boolean;
    /** Title for the banner when feature is available */
    bannerTitle?: string;
    /** Subtitle for the banner when feature is available */
    bannerSubtitle?: string;
    /** Title for the banner when feature is NOT available (upgrade case) */
    upgradeBannerTitle?: string;
    /** Subtitle for the banner when feature is NOT available (upgrade case) */
    upgradeBannerSubtitle?: string;
    /** Custom style for the banner card */
    bannerStyle?: CSSProperties;
    /** Custom className for the banner card */
    bannerClassName?: string;
}

/**
 * Component that conditionally renders children based on feature availability.
 * Shows upgrade CTA or fallback content when feature is not available.
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
    feature,
    fallback,
    showUpgrade = true,
    upgradeSource,
    upgradeCallToAction,
    buttonText,
    children,
    showBanner = false,
    bannerTitle,
    bannerSubtitle,
    upgradeBannerTitle,
    upgradeBannerSubtitle,
    bannerStyle,
    bannerClassName
}) => {
    const { isAvailable, getUpgradeMessage, minRequiredPlan, availablePlans } = useFeature(feature);

    // Regular mode - original behavior
    if (isAvailable) {
        return <>{children}</>;
    }

    // if(children) {
    //     return (
    //         <div>
    //             {children}
    //         </div>
    //     )
    // }
    // Banner mode - wrap content in CardSymphony
    if (showBanner) {

        // Feature not available - show upgrade banner
        const upgradeMessage = getUpgradeMessage();
        const upgradeTitle = upgradeBannerTitle || upgradeMessage;
        const upgradeSubtitle = upgradeBannerSubtitle || `Get access to advanced features and more.`;
        console.log('show banner')
        return (
            <CardSymphony
                className="featureGateBanner"
                title={upgradeTitle}
                subtitle={upgradeSubtitle}
                size="small"
                style={{ backgroundColor: Colors.purpleLighter }}
            >
                <UpgradeToUnlockCTA
                    highlightedPlan={minRequiredPlan || PlanType.PRO}
                    onlyShowPlans={availablePlans as PlanType[]}
                    requiresPaidPlan={true}
                    upgradeSource={upgradeSource || `Feature Gate Banner - ${feature}`}
                    buttonText={buttonText || 'Upgrade'}
                    upgradePopupCallToAction={upgradeCallToAction || getUpgradeMessage()}
                />
            </CardSymphony>
        );
    } else {
        return (
            <UpgradeToUnlockCTA
                highlightedPlan={minRequiredPlan || PlanType.PRO}
                onlyShowPlans={availablePlans as PlanType[]}
                requiresPaidPlan={true}
                upgradeSource={upgradeSource || `Feature Gate - ${feature}`}
                buttonText={buttonText || 'Upgrade'}
                upgradePopupCallToAction={upgradeCallToAction || getUpgradeMessage()}
            />
        );
    }
};

export default FeatureGate; 